import { useEffect, useState } from 'react';
import { ouvrirSession, connexionLocale } from './discord/auth';
import { obtenirSdk } from './discord/sdk';
import { useSeance } from './sync/useSeance';
import { useProjection } from './sync/useProjection';
import { useDirect } from './sync/useDirect';
import { usePresence } from './discord/usePresence';
import { DANS_DISCORD } from './api/client';
import Chargement from './ecrans/Chargement';
import NonLie from './ecrans/NonLie';
import Erreur from './ecrans/Erreur';
import ConnexionLocale from './ecrans/ConnexionLocale';
import Catalogue from './ecrans/Catalogue';
import Attente from './ecrans/Attente';
import SalleAttente from './ecrans/SalleAttente';
import Lecteur from './ecrans/Lecteur';
import Direct from './ecrans/Direct';
import AttenteDirect from './ecrans/AttenteDirect';

/**
 * L'aiguillage : un seul écran à la fois, selon l'état de la session.
 *
 * Volontairement sans routeur. Une Activity n'a pas d'URL à partager ni de
 * bouton « précédent » : y mettre un routeur reviendrait à installer une
 * mécanique pour un besoin qui n'existe pas.
 */
export default function App() {
  const [session, setSession] = useState({ etat: 'chargement' });
  const [contexte, setContexte] = useState({ instanceId: null, channelId: null });

  useEffect(() => {
    let vivant = true;

    ouvrirSession()
      .then(async (resultat) => {
        if (!vivant) return;
        const sdk = await obtenirSdk();
        // L'instance identifie la séance spontanée, le salon la projection
        // programmée : le bot connaît le second des jours à l'avance, jamais
        // la première.
        setContexte({ instanceId: sdk.instanceId, channelId: sdk.channelId });
        setSession(resultat);
      })
      .catch((erreur) => {
        console.error('Ouverture de session :', erreur);
        if (vivant) setSession({ etat: 'erreur', message: erreur.message });
      });

    return () => { vivant = false; };
  }, []);

  switch (session.etat) {
    case 'chargement':
      return <Chargement texte={DANS_DISCORD ? 'Connexion à Discord…' : 'Démarrage…'} />;

    case 'connexion-locale':
      return (
        <ConnexionLocale
          onConnexion={async (identifiant, motdepasse) => {
            const resultat = await connexionLocale(identifiant, motdepasse);
            const sdk = await obtenirSdk();
            setContexte({ instanceId: sdk.instanceId, channelId: sdk.channelId });
            setSession(resultat);
          }}
        />
      );

    case 'non-lie':
      return <NonLie discord={session.discord} raison={session.raison} message={session.message} />;

    case 'pret':
      return <Salle utilisateur={session.utilisateur} contexte={contexte} />;

    default:
      return <Erreur message={session.message} onReessayer={() => window.location.reload()} />;
  }
}

/**
 * Trois régimes cohabitent, et ils se départagent par leur degré d'actualité.
 *
 * **Direct** : quelqu'un émet, en ce moment même. C'est le seul régime dont
 * l'existence prouve qu'une personne est en train d'agir — un direct ne peut
 * pas traîner par oubli, il s'éteint de lui-même vingt secondes après le
 * dernier segment reçu. Il l'emporte donc sur tout le reste : s'il est là,
 * c'est que quelqu'un vient de l'allumer.
 *
 * **Programmée** : une heure de départ, personne aux commandes. C'est le
 * modèle de la salle de cinéma — on entre, le film tourne, on n'a rien à
 * décider. Elle passe avant la séance spontanée parce qu'elle a été annoncée :
 * si quelqu'un pouvait lancer autre chose par-dessus, l'annonce ne voudrait
 * plus rien dire.
 *
 * **Spontanée** : le premier arrivé commande, avec passage de relais. C'est ce
 * qui se passe le reste du temps.
 */
function Salle({ utilisateur, contexte }) {
  const seance = useSeance({ instanceId: contexte.instanceId, utilisateur });
  const { projection, chargee, positionAttendue, attenteRestante } =
    useProjection({ channelId: contexte.channelId });
  const { direct, chargee: directChargee } = useDirect({ channelId: contexte.channelId });

  const filmProjete = projection
    ? { id: projection.movie_id, title: projection.movie_title, poster: projection.movie_poster }
    : null;

  // Le statut Discord suit ce qui est réellement à l'écran, et l'ordre des
  // replis est le même que celui de l'aiguillage plus bas : ce qui s'affiche
  // et ce qui s'annonce ne doivent jamais diverger.
  usePresence({
    film: direct?.on_air
      ? { id: `direct-${direct.id}`, title: direct.title }
      : (filmProjete ?? seance.film),
    enLecture: direct?.on_air ? true : (projection ? Boolean(projection.has_started) : Boolean(seance.etat?.is_playing)),
    // Pour un direct, c'est le temps d'antenne : le compteur de Discord
    // affiche depuis combien de temps ça émet, et non une position dans un
    // fichier qui n'existe pas.
    position: direct?.on_air ? direct.since : (projection ? projection.position : (seance.etat?.position ?? 0)),
    nomDuMaitre: projection ? projection.created_by_name : seance.nomDuMaitre,
    enDirect: Boolean(direct?.on_air),
  });

  if (!chargee || !directChargee || !seance.etat) return <Chargement texte="Rejoindre la séance…" />;

  // Le direct passe devant, sans condition. Il n'a ni position ni maître : le
  // lui présenter comme une séance n'apporterait rien, et lui inventer une
  // horloge lui ferait dire le contraire de ce qu'il est.
  if (direct?.on_air) return <Direct key={`direct-${direct.id}`} direct={direct} />;

  // Annoncé mais pas encore commencé : on tient la salle plutôt que de
  // renvoyer au catalogue quelqu'un venu pour ça. L'heure ne déclenche rien —
  // c'est l'arrivée des segments qui fera basculer l'écran.
  if (direct?.waiting) return <AttenteDirect direct={direct} />;

  if (projection) {
    if (!projection.has_started) {
      return <SalleAttente projection={projection} attenteRestante={attenteRestante} />;
    }

    return (
      <Lecteur
        key={`projection-${projection.id}`}
        film={filmProjete}
        utilisateur={utilisateur}
        seance={adapterProjection(projection, positionAttendue)}
      />
    );
  }

  // Le catalogue n'est montré qu'à celui qui commande. Le laisser à tous
  // revenait à laisser n'importe qui lancer un film à la place de celui qui
  // avait ouvert la séance.
  if (!seance.film) {
    return seance.jeSuisMaitre ? (
      <Catalogue onChoisir={seance.lancerFilm} />
    ) : (
      <Attente
        nomDuMaitre={seance.nomDuMaitre}
        maitreAbsent={seance.maitreAbsent}
        onPrendreLaMain={seance.prendreLaMain}
      />
    );
  }

  return (
    <Lecteur
      key={seance.film.id}
      film={seance.film}
      utilisateur={utilisateur}
      seance={seance}
      onRetour={seance.jeSuisMaitre ? seance.quitterLeFilm : undefined}
    />
  );
}

/**
 * Une projection présentée au lecteur comme une séance dont personne n'est le
 * maître. Le lecteur n'a ainsi qu'un seul mode de fonctionnement — il suit — et
 * n'a pas à connaître la différence entre les deux régimes.
 */
function adapterProjection(projection, positionAttendue) {
  const debut = new Date(projection.scheduled_at);

  return {
    projection: true,
    heureDeDepart: debut.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    etat: { is_playing: true, position: projection.position },
    jeSuisMaitre: false,
    maitreAbsent: false,
    nomDuMaitre: projection.created_by_name,
    positionAttendue,
    // L'horloge décide : il n'y a rien à pousser, et rien à reprendre.
    pousser: () => {},
    prendreLaMain: () => {},
  };
}
